import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import type { DashboardUseCase } from "../../../types/use-case.types"

import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"

import { SITE_CHART_COLORS } from "../../../types/use-case.types"
import { formatNumber, formatUSD } from "../../../utils/format-helpers"

type SiteSummaryTableProps = {
  siteMap: Map<string, DashboardUseCase[]>
  allUseCases: DashboardUseCase[]
}

type SiteRow = {
  site: string
  count: number
  investment: number
  opCost: number
  hours: number
  benefit: number
  hardSavings: number
  roi: number
  color: string
}

export function SiteSummaryTable({ siteMap, allUseCases }: SiteSummaryTableProps) {
  const { t } = useTranslation()

  const { rows, totals } = useMemo(() => {
    const sites = [...siteMap.keys()].sort()
    const siteRows: SiteRow[] = sites.map((site, i) => {
      const ucs = siteMap.get(site) || []
      let investment = 0
      let opCost = 0
      let hours = 0
      let benefit = 0
      let hardSavings = 0
      let roi = 0
      for (const uc of ucs) {
        investment += parseFloat(uc.investment) || 0
        opCost += uc.opCostYearly
        hours += uc.totalCapacityHours
        benefit += uc.annualBenefit
        hardSavings += uc.hardSavings
        roi += uc.roi12m
      }
      return {
        site,
        count: ucs.length,
        investment,
        opCost,
        hours,
        benefit,
        hardSavings,
        roi,
        color: SITE_CHART_COLORS[i % SITE_CHART_COLORS.length],
      }
    })

    // "All Sites (unique)" totals — each use case counted once
    let tInvestment = 0
    let tOpCost = 0
    let tHours = 0
    let tBenefit = 0
    let tHardSavings = 0
    let tRoi = 0
    for (const uc of allUseCases) {
      tInvestment += parseFloat(uc.investment) || 0
      tOpCost += uc.opCostYearly
      tHours += uc.totalCapacityHours
      tBenefit += uc.annualBenefit
      tHardSavings += uc.hardSavings
      tRoi += uc.roi12m
    }

    return {
      rows: siteRows,
      totals: {
        count: allUseCases.length,
        investment: tInvestment,
        opCost: tOpCost,
        hours: tHours,
        benefit: tBenefit,
        hardSavings: tHardSavings,
        roi: tRoi,
      },
    }
  }, [siteMap, allUseCases])

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="px-4 py-2">{t("useCases.dashboard.siteComparison.site")}</TableHead>
          <TableHead className="px-4 py-2 text-right">
            {t("useCases.dashboard.siteComparison.count")}
          </TableHead>
          <TableHead className="px-4 py-2 text-right">
            {t("useCases.dashboard.table.investment")}
          </TableHead>
          <TableHead className="px-4 py-2 text-right">
            {t("useCases.dashboard.table.opCost")}
          </TableHead>
          <TableHead className="px-4 py-2 text-right">
            {t("useCases.dashboard.table.hours")}
          </TableHead>
          <TableHead className="px-4 py-2 text-right">
            {t("useCases.dashboard.table.annualBenefit")}
          </TableHead>
          <TableHead className="px-4 py-2 text-right">
            {t("useCases.dashboard.siteComparison.hardSavings")}
          </TableHead>
          <TableHead className="px-4 py-2 text-right">
            {t("useCases.dashboard.table.roi")}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.site}>
            <TableCell className="px-4 py-2">
              <div className="flex items-center gap-2">
                <div
                  className="border-foreground size-2.5 rounded-full border"
                  style={{ backgroundColor: row.color }}
                />
                {row.site}
              </div>
            </TableCell>
            <TableCell className="px-4 py-2 text-right tabular-nums">{row.count}</TableCell>
            <TableCell className="px-4 py-2 text-right tabular-nums">
              {formatUSD(row.investment)}
            </TableCell>
            <TableCell className="px-4 py-2 text-right tabular-nums">
              {formatUSD(row.opCost)}
            </TableCell>
            <TableCell className="px-4 py-2 text-right tabular-nums">
              {formatNumber(Math.round(row.hours))}
            </TableCell>
            <TableCell className="px-4 py-2 text-right tabular-nums">
              {formatUSD(row.benefit)}
            </TableCell>
            <TableCell className="px-4 py-2 text-right tabular-nums">
              {formatUSD(row.hardSavings)}
            </TableCell>
            <TableCell
              className={cn(
                "px-4 py-2 text-right tabular-nums",
                row.roi >= 0 ? "text-success" : "text-destructive"
              )}
            >
              {formatUSD(row.roi)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter>
        <TableRow className="bg-muted font-semibold">
          <TableCell className="px-4 py-2">
            {t("useCases.dashboard.siteComparison.allSites")} ({totals.count})
          </TableCell>
          <TableCell className="px-4 py-2 text-right tabular-nums">{totals.count}</TableCell>
          <TableCell className="px-4 py-2 text-right tabular-nums">
            {formatUSD(totals.investment)}
          </TableCell>
          <TableCell className="px-4 py-2 text-right tabular-nums">
            {formatUSD(totals.opCost)}
          </TableCell>
          <TableCell className="px-4 py-2 text-right tabular-nums">
            {formatNumber(Math.round(totals.hours))}
          </TableCell>
          <TableCell className="px-4 py-2 text-right tabular-nums">
            {formatUSD(totals.benefit)}
          </TableCell>
          <TableCell className="px-4 py-2 text-right tabular-nums">
            {formatUSD(totals.hardSavings)}
          </TableCell>
          <TableCell
            className={cn(
              "px-4 py-2 text-right tabular-nums",
              totals.roi >= 0 ? "text-success" : "text-destructive"
            )}
          >
            {formatUSD(totals.roi)}
          </TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  )
}
